/**
 * Knowledge Vault Component
 *
 * Allows users to upload documents, view their knowledge base,
 * and search for relevant content.
 *
 * Integrates with:
 * - POST /api/v1/knowledge/upload - Upload documents
 * - POST /api/v1/knowledge/ingest-text - Add raw text
 * - POST /api/v1/knowledge/search - Semantic search
 * - GET /api/v1/knowledge/documents - List documents
 * - DELETE /api/v1/knowledge/document/:id - Delete document
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  Search,
  FileText,
  File,
  Trash2,
  Plus,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Database,
  FolderOpen,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface KnowledgeVaultProps {
  brandProfileId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

interface Document {
  id: string;
  filename: string;
  source_type: string;
  chunk_count: number;
  created_at: string;
  metadata?: Record<string, unknown>;
}

interface SearchResult {
  text: string;
  score: number;
  filename: string;
  chunk_index: number;
  metadata?: Record<string, unknown>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const KnowledgeVault: React.FC<KnowledgeVaultProps> = ({
  brandProfileId,
  getAuthHeaders,
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'documents' | 'search'>('documents');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Text input state
  const [showTextInput, setShowTextInput] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [textSourceName, setTextSourceName] = useState('');

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, [brandProfileId]);

  const loadDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/api/v1/knowledge/documents?brand_profile_id=${brandProfileId}`,
        { headers }
      );
      if (!response.ok) throw new Error('Failed to load documents');
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // File upload with dropzone
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setIsUploading(true);
      setError(null);
      setSuccess(null);

      try {
        const headers = await getAuthHeaders();
        let uploadedCount = 0;

        for (const file of acceptedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('brand_profile_id', brandProfileId);

          const response = await fetch(`${API_BASE}/api/v1/knowledge/upload`, {
            method: 'POST',
            headers: {
              Authorization: headers.Authorization,
            },
            body: formData,
          });

          if (response.ok) {
            uploadedCount++;
          } else {
            const errData = await response.json();
            throw new Error(errData.detail || `Failed to upload ${file.name}`);
          }
        }

        setSuccess(`Successfully uploaded ${uploadedCount} document(s)`);
        await loadDocuments();
        setActiveTab('documents');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setIsUploading(false);
      }
    },
    [brandProfileId, getAuthHeaders]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md', '.markdown'],
      'text/html': ['.html', '.htm'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isUploading,
  });

  // Text ingestion
  const handleTextSubmit = async () => {
    if (!textContent.trim() || textContent.length < 10) {
      setError('Text must be at least 10 characters');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/v1/knowledge/ingest-text`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_profile_id: brandProfileId,
          text: textContent,
          source_name: textSourceName || 'Direct Input',
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to ingest text');
      }

      setSuccess('Text ingested successfully');
      setTextContent('');
      setTextSourceName('');
      setShowTextInput(false);
      await loadDocuments();
      setActiveTab('documents');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Text ingestion failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Search
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setError('Search query must be at least 3 characters');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResults([]);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/v1/knowledge/search`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_profile_id: brandProfileId,
          query: searchQuery,
          top_k: 10,
          score_threshold: 0.5,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Search failed');
      }

      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete document
  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    setIsLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/api/v1/knowledge/document/${documentId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Delete failed');
      }

      setSuccess('Document deleted successfully');
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'docx':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'md':
      case 'markdown':
        return <FileText className="w-5 h-5 text-purple-500" />;
      default:
        return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Database className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Knowledge Vault</h2>
              <p className="text-sm text-gray-500">
                Upload documents to enhance AI-generated content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <BookOpen className="w-4 h-4" />
            {documents.length} document{documents.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { id: 'documents', label: 'Documents', icon: FolderOpen },
          { id: 'upload', label: 'Upload', icon: Upload },
          { id: 'search', label: 'Search', icon: Search },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
              activeTab === tab.id
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-500" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-green-50 border-b border-green-100 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-700">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4 text-green-500" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className="p-6">
        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            {isLoading && documents.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No documents yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Upload brand guidelines, style guides, or reference materials
                </p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  <Upload className="w-4 h-4" />
                  Upload Documents
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-3">
                      {getFileIcon(doc.filename)}
                      <div>
                        <p className="font-medium text-gray-900">{doc.filename}</p>
                        <p className="text-sm text-gray-500">
                          {doc.chunk_count} chunks • Added{' '}
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      disabled={isLoading}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* File Upload */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
                isDragActive
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
              } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <Loader2 className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-spin" />
              ) : (
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              )}
              <p className="text-lg font-medium text-gray-900 mb-2">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-gray-500">
                or click to browse • PDF, DOCX, TXT, MD, HTML • Max 10MB
              </p>
            </div>

            {/* Text Input Toggle */}
            <div className="text-center">
              <button
                onClick={() => setShowTextInput(!showTextInput)}
                className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
              >
                <Plus className="w-4 h-4" />
                Or paste text directly
              </button>
            </div>

            {/* Text Input Form */}
            <AnimatePresence>
              {showTextInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 p-4 bg-gray-50 rounded-xl"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source Name (optional)
                    </label>
                    <input
                      type="text"
                      value={textSourceName}
                      onChange={(e) => setTextSourceName(e.target.value)}
                      placeholder="e.g., Brand Guidelines, Style Guide"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value)}
                      placeholder="Paste your brand guidelines, style notes, or any reference content..."
                      rows={6}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowTextInput(false);
                        setTextContent('');
                        setTextSourceName('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleTextSubmit}
                      disabled={isUploading || textContent.length < 10}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {isUploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      Add to Knowledge Vault
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="space-y-6">
            {/* Search Input */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search your knowledge base..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isLoading || searchQuery.length < 3}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Search'
                )}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Found {searchResults.length} relevant passage{searchResults.length !== 1 ? 's' : ''}
                </p>
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <FileText className="w-4 h-4" />
                        {result.filename}
                      </div>
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                        {Math.round(result.score * 100)}% match
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {result.text}
                    </p>
                  </div>
                ))}
              </div>
            ) : searchQuery && !isLoading ? (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Search your uploaded documents for relevant content
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeVault;
